import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AiOutlineSafety, AiOutlineDownload, AiOutlineReload, AiOutlineCheckCircle, AiOutlineWarning, AiOutlineCloseCircle } from 'react-icons/ai';
import useUser from '../hooks/useUser';
import { useSignatureClientService } from '../services/api/signatureClientService';
import { getLocalClientStatus } from '../services/localSignatureClient';
import { getAuthorizationStatus, listTokens } from '../services/signatureDaemonClient';
import { compareVersions, detectOs, isOutdated } from '../utils/version';
import { useNotification } from '../contexts/NotificationContext';
import { ENV } from '../config/env';

/**
 * Pantalla "Cliente local de firma" — Mi perfil → Firma digital (SOP-015 / FSD-015, #59 F1B).
 *
 * Implementa el procedimiento operativo de instalar/verificar/actualizar el cliente local:
 *  - REQ-002: requisitos técnicos (SO, navegadores, drivers PKCS#11).
 *  - REQ-003: descarga del instalador del SO detectado + checksum SHA-256.
 *  - REQ-006: "Probar cliente local" → versión del cliente, tokens y certificados; marca si el
 *    subject del cert corresponde al usuario autenticado.
 *  - REQ-007/008: estado de actualización (instalada vs vigente) + guía auto/manual.
 *
 * Gated a ROLE_SIGNER por la ruta (ProtectedRoute requiredRole="SIGNER", REQ-001).
 */

const OS_LABELS = { windows: 'Windows', macos: 'macOS', linux: 'Linux' };

const MAX_CONSENT_WAIT_MS = 60000;
const CONSENT_POLL_MS = 2000;

// Matriz de requisitos publicada (REQ-002). Windows es el SO soportado hoy; macOS/Linux postergados
// hasta que un cliente los pida (F1B-5). Drivers PKCS#11 por vendor de token habitual en AR.
const REQUIREMENTS = {
  os: ['Windows 10 / 11 (64-bit).', 'macOS y Linux: postergados (se publicarán cuando se requieran).'],
  browsers: ['Microsoft Edge (recomendado).', 'Google Chrome.', 'Mozilla Firefox.'],
  drivers: [
    'mToken CryptoIDE (AR): cryptoide_pkcs11.dll (instalado por el proveedor del token).',
    'SafeNet eToken: eTPKCS11.dll.',
    'Athena/IDProtect: asepkcs.dll.',
    'Feitian: eps2003csp11.dll.',
  ],
  notes: [
    'La instalación del cliente requiere permisos de administrador en la estación.',
    'Los drivers PKCS#11 del token y su certificado son responsabilidad del proveedor / la AC emisora.',
  ],
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

/** Best-effort: ¿el subject del cert corresponde al usuario autenticado? Compara el CUIL del
 *  claim del usuario contra los dígitos presentes en el subject (SERIALNUMBER=CUIL …). */
const certMatchesUser = (subject, userCuil) => {
  if (!subject || !userCuil) return null; // desconocido → no afirmar nada
  const digits = String(userCuil).replace(/\D/g, '');
  if (!digits) return null;
  return subject.replace(/\D/g, '').includes(digits);
};

const SignatureClientPage = () => {
  const { user } = useUser();
  const service = useSignatureClientService();
  const { notify } = useNotification();

  const os = detectOs();
  const userCuil = user?.profile?.cuil || null;

  const [manifest, setManifest] = useState(null);
  const [manifestLoaded, setManifestLoaded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Estado del "Probar cliente local"
  const [probe, setProbe] = useState({ state: 'idle', clientVersion: null, tokens: null, error: null });
  const [pin, setPin] = useState('');
  const consentPollRef = useRef(null); // timeout del poll de consentimiento; cancela en unmount (I6)

  useEffect(() => {
    let alive = true;
    service.getManifest()
      .then((m) => { if (alive) { setManifest(m); setManifestLoaded(true); } })
      .catch(() => { if (alive) setManifestLoaded(true); });
    return () => { alive = false; };
    // service es una instancia nueva por render; corremos una sola vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancelar el poll de consentimiento al desmontar la página (I6, ADR-033).
  useEffect(() => {
    return () => {
      if (consentPollRef.current) { clearTimeout(consentPollRef.current); consentPollRef.current = null; }
    };
  }, []);

  const platform = os && manifest?.platforms ? manifest.platforms[os] : null;
  const latest = manifest?.currentVersion || null;
  const installed = probe.clientVersion;

  const handleDownload = useCallback(async () => {
    if (!platform?.downloadUrl) return;
    setDownloading(true);
    try {
      await service.downloadInstaller(platform.downloadUrl, platform.filename);
      notify.success('Descarga del instalador iniciada.');
    } catch {
      notify.error('No se pudo descargar el instalador. Reintentá o contactá a soporte.');
    } finally {
      setDownloading(false);
    }
  }, [platform, service, notify]);

  // REQ-006: detectar cliente + autorización por-origen + (con PIN) tokens y certs.
  const handleProbeClient = useCallback(async () => {
    // Cancelar cualquier poll previo antes de iniciar una nueva secuencia (I6).
    if (consentPollRef.current) { clearTimeout(consentPollRef.current); consentPollRef.current = null; }
    setProbe({ state: 'checking', clientVersion: null, tokens: null, error: null });
    let status;
    try {
      status = await getLocalClientStatus();
    } catch {
      setProbe({ state: 'not_detected', clientVersion: null, tokens: null, error: null });
      return;
    }
    if (!status) {
      setProbe({ state: 'not_detected', clientVersion: null, tokens: null, error: null });
      return;
    }
    // Verificar versión mínima (floor = minimumVersion del manifest, o currentVersion como provisional)
    const floor = manifest?.minimumVersion || manifest?.currentVersion;
    if (floor && status.version && compareVersions(status.version, floor) < 0) {
      setProbe({ state: 'outdated', clientVersion: status.version, tokens: null, error: null });
      return;
    }
    // Autorización por-origen: consentimiento nativo una sola vez (ADR-033).
    // El timeout id se guarda en consentPollRef para poder cancelarlo si la página se desmonta
    // o el usuario relanza el probe antes de que expire el poll (I6, ADR-033).
    const started = Date.now();
    const pollAuthorize = async () => {
      let auth;
      try {
        auth = await getAuthorizationStatus();
      } catch {
        setProbe({ state: 'not_detected', clientVersion: null, tokens: null, error: null });
        return;
      }
      if (auth.authorized) {
        setProbe({ state: 'detected', clientVersion: status.version, tokens: null, error: null });
        return;
      }
      setProbe({ state: 'awaiting_consent', clientVersion: status.version, tokens: null, error: null });
      if (Date.now() - started > MAX_CONSENT_WAIT_MS) {
        setProbe({ state: 'consent_timeout', clientVersion: status.version, tokens: null, error: null });
        return;
      }
      consentPollRef.current = setTimeout(pollAuthorize, CONSENT_POLL_MS);
    };
    await pollAuthorize();
  }, [manifest]);

  const handleListCerts = useCallback(async () => {
    setProbe((p) => ({ ...p, state: 'listing', error: null }));
    try {
      const tokens = await listTokens({ pin });
      setProbe((p) => ({ ...p, state: 'listed', tokens: tokens || [] }));
    } catch (e) {
      setProbe((p) => ({ ...p, state: 'detected', error: e?.message || 'No se pudieron leer los certificados del token.' }));
    }
  }, [pin]);

  const updateStatus = manifestLoaded && latest && installed
    ? (isOutdated(installed, latest) ? 'outdated' : 'updated')
    : null;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <AiOutlineSafety style={{ fontSize: 30, color: '#00468b' }} />
        <h1 style={styles.h1}>Cliente local de firma</h1>
      </div>
      <p style={styles.intro}>
        El cliente local opera tu token criptográfico desde el navegador para firmar documentos en Bóveda.
        Descargalo, instalalo y verificá que quede operativo siguiendo los pasos de abajo (SOP-015).
      </p>

      {/* REQ-002 — Requisitos técnicos */}
      <section style={styles.card}>
        <h2 style={styles.h2}>1 · Requisitos técnicos</h2>
        <div style={styles.reqGrid}>
          <div>
            <h3 style={styles.h3}>Sistema operativo</h3>
            <ul style={styles.ul}>{REQUIREMENTS.os.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
          <div>
            <h3 style={styles.h3}>Navegadores</h3>
            <ul style={styles.ul}>{REQUIREMENTS.browsers.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
          <div>
            <h3 style={styles.h3}>Drivers PKCS#11 del token</h3>
            <ul style={styles.ul}>{REQUIREMENTS.drivers.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        </div>
        <ul style={{ ...styles.ul, marginTop: 8, color: '#666' }}>
          {REQUIREMENTS.notes.map((x) => <li key={x}>{x}</li>)}
        </ul>
      </section>

      {/* REQ-003 — Descargar instalador */}
      <section style={styles.card}>
        <h2 style={styles.h2}>2 · Descargar instalador</h2>
        {!manifestLoaded && <p style={styles.muted}>Consultando la versión vigente…</p>}
        {manifestLoaded && !latest && (
          <p style={styles.muted}>
            Todavía no hay una versión del cliente publicada. Pedí al administrador que publique un release,
            o contactá a soporte.
          </p>
        )}
        {manifestLoaded && latest && (
          <>
            <div style={styles.kv}><span>Versión vigente</span><strong>{latest}</strong></div>
            <div style={styles.kv}><span>Publicada</span><span>{fmtDate(manifest?.releasedAt)}</span></div>
            <div style={styles.kv}><span>Tu sistema operativo</span><span>{os ? OS_LABELS[os] : 'No detectado'}</span></div>
            {platform ? (
              <>
                <div style={styles.kv}><span>Instalador</span><span>{platform.filename}</span></div>
                <div style={styles.kv}><span>Checksum SHA-256</span><code style={styles.code}>{platform.checksum || '—'}</code></div>
                <button type="button" onClick={handleDownload} disabled={downloading} style={styles.primaryBtn}>
                  <AiOutlineDownload /> {downloading ? 'Descargando…' : 'Descargar instalador'}
                </button>
                <p style={styles.hint}>
                  Verificá el checksum del archivo descargado, ejecutalo con permisos de administrador y reiniciá el navegador.
                </p>
              </>
            ) : (
              <p style={styles.warn}>
                <AiOutlineWarning /> Tu sistema operativo {os ? `(${OS_LABELS[os]}) ` : ''}no tiene instalador publicado.
                Sistemas disponibles: {Object.keys(manifest?.platforms || {}).map((k) => OS_LABELS[k] || k).join(', ') || '—'}.
              </p>
            )}
          </>
        )}
      </section>

      {/* REQ-006 — Probar cliente local */}
      <section style={styles.card}>
        <h2 style={styles.h2}>3 · Probar cliente local</h2>
        <p style={styles.muted}>Conectá el token al puerto USB y verificá que el cliente lo detecta.</p>
        <button type="button" onClick={handleProbeClient} disabled={probe.state === 'checking'} style={styles.secondaryBtn}>
          <AiOutlineReload /> {probe.state === 'checking' ? 'Probando…' : 'Probar cliente local'}
        </button>

        {probe.state === 'not_detected' && (
          <div style={styles.errorBox}>
            <AiOutlineCloseCircle /> Cliente local no detectado. Verificá que esté corriendo (icono en la barra de tareas),
            reinicialo, y si persiste reinstalá desde el paso 2. Si actualizaste Bóveda recientemente, reinstalá el cliente.
          </div>
        )}

        {probe.state === 'outdated' && (
          <div style={styles.warnBox}>
            <AiOutlineWarning /> El cliente de firma está desactualizado (versión {probe.clientVersion}). Reinstalalo desde el paso 2.
          </div>
        )}

        {probe.state === 'awaiting_consent' && (
          <div style={styles.warnBox}>
            <AiOutlineWarning /> Autorizá este sitio en la ventana del cliente de firma, después reintentá.
          </div>
        )}

        {probe.state === 'consent_timeout' && (
          <div style={styles.errorBox}>
            <AiOutlineCloseCircle /> No se recibió la autorización a tiempo.{' '}
            <button type="button" onClick={handleProbeClient} style={{ marginLeft: 8, cursor: 'pointer' }}>Reintentar</button>
          </div>
        )}

        {['detected', 'listing', 'listed'].includes(probe.state) && (
          <div style={styles.okBox}>
            <AiOutlineCheckCircle /> Cliente local detectado — versión <strong>{probe.clientVersion || 'desconocida'}</strong>.
          </div>
        )}

        {['detected', 'listing', 'listed'].includes(probe.state) && (
          <div style={styles.pinRow}>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="PIN del token"
              aria-label="PIN del token"
              style={styles.pinInput}
            />
            <button type="button" onClick={handleListCerts} disabled={!pin || probe.state === 'listing'} style={styles.secondaryBtn}>
              {probe.state === 'listing' ? 'Leyendo token…' : 'Ver tokens y certificados'}
            </button>
          </div>
        )}

        {probe.error && <div style={styles.errorBox}><AiOutlineCloseCircle /> {probe.error}</div>}

        {probe.state === 'listed' && Array.isArray(probe.tokens) && probe.tokens.length === 0 && (
          <div style={styles.errorBox}>
            <AiOutlineWarning /> No se detectaron tokens. Verificá la conexión USB / probá otro puerto y que los drivers PKCS#11 estén instalados.
          </div>
        )}

        {probe.state === 'listed' && Array.isArray(probe.tokens) && probe.tokens.map((c, i) => {
          const match = certMatchesUser(c.subject, userCuil);
          return (
            <div key={c.alias || c.serial || i} style={styles.certCard}>
              <div style={styles.kv}><span>Certificado</span><strong>{c.subject || c.alias || '—'}</strong></div>
              <div style={styles.kv}><span>Emisor (AC)</span><span>{c.issuer || '—'}</span></div>
              <div style={styles.kv}><span>Vigencia</span><span>{fmtDate(c.notBefore)} → {fmtDate(c.notAfter)}</span></div>
              {match === true && <div style={styles.okBox}><AiOutlineCheckCircle /> El certificado corresponde a tu usuario.</div>}
              {match === false && (
                <div style={styles.warnBox}>
                  <AiOutlineWarning /> El subject de este certificado no coincide con tu usuario. Si la serie en la que vas
                  a firmar valida la identidad del firmante, {ENV.APP_NAME} podría rechazar la firma. Verificá que estás
                  usando tu token (o consultá con el administrador si esa serie no valida identidad).
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* REQ-007/008 — Actualización */}
      <section style={styles.card}>
        <h2 style={styles.h2}>4 · Actualización</h2>
        {updateStatus === 'updated' && (
          <div style={styles.okBox}><AiOutlineCheckCircle /> El cliente está actualizado (versión {installed}).</div>
        )}
        {updateStatus === 'outdated' && (
          <div style={styles.warnBox}>
            <AiOutlineWarning /> Hay una versión más nueva ({latest}); tenés instalada la {installed}. Descargala desde el paso 2 e instalala.
          </div>
        )}
        {!updateStatus && <p style={styles.muted}>Probá el cliente local (paso 3) para comparar la versión instalada con la vigente.</p>}
        <p style={styles.hint}>
          El cliente busca actualizaciones automáticamente al iniciar. Para forzar: abrí el cliente (icono en la barra de tareas)
          → "Buscar actualizaciones". Si el auto-update falla repetidamente, descargá la última versión del paso 2 y reinstalá.
        </p>
      </section>
    </div>
  );
};

const styles = {
  page: { padding: '24px', maxWidth: 880, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 },
  h1: { fontSize: 26, margin: 0 },
  intro: { color: '#555', marginTop: 0 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 18 },
  h2: { fontSize: 18, marginTop: 0, marginBottom: 14, color: '#00468b' },
  h3: { fontSize: 14, margin: '0 0 6px' },
  reqGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 },
  ul: { margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 },
  kv: { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14 },
  code: { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', textAlign: 'right' },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 14, padding: '8px 16px', background: '#00468b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  secondaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 14px', background: '#eef2f7', color: '#00468b', border: '1px solid #cdd9e6', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  pinRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 },
  pinInput: { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, width: 180 },
  muted: { color: '#777', fontSize: 14 },
  hint: { color: '#888', fontSize: 13, marginTop: 10 },
  warn: { color: '#92500a', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 },
  okBox: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '8px 12px', background: '#ecfdf3', border: '1px solid #abefc6', borderRadius: 6, color: '#067647', fontSize: 14 },
  warnBox: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '8px 12px', background: '#fff4e5', border: '1px solid #f0c98a', borderRadius: 6, color: '#7a4f01', fontSize: 14 },
  errorBox: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '8px 12px', background: '#fef3f2', border: '1px solid #fecdc9', borderRadius: 6, color: '#b42318', fontSize: 14 },
  certCard: { marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fafafa' },
};

export default SignatureClientPage;
